import { useEffect, useCallback } from "react";
import { useDocumentStore } from "../stores/document";
import { useUIStore } from "../stores/ui";

export type ShortcutKey =
  | "save"
  | "compile"
  | "toggleFocusMode"
  | "toggleSidebar"
  | "toggleBottomPanel"
  | "toggleTheme"
  | "exitFocusMode";

export type ShortcutHandlers = Partial<Record<ShortcutKey, () => void>>;

const DEFAULT_HANDLERS: Record<ShortcutKey, () => void> = {
  save: () => {},
  compile: () => {},
  toggleFocusMode: () => {},
  toggleSidebar: () => {},
  toggleBottomPanel: () => {},
  toggleTheme: () => {},
  exitFocusMode: () => {},
};

/**
 * Keyboard shortcuts hook. Accepts an optional map of shortcut handlers to override defaults.
 * Default shortcuts:
 * - Cmd+S: save document
 * - Cmd+Enter: compile (LaTeX)
 * - Cmd+Shift+F: toggle focus mode
 * - Cmd+B: toggle sidebar
 * - Cmd+J: toggle bottom panel
 * - Cmd+\: toggle theme
 * - Escape: exit focus mode
 */
export function useKeyboardShortcuts(customHandlers?: ShortcutHandlers) {
  const saveDocument = useDocumentStore((s) => s.saveDocument);
  const compile = useDocumentStore((s) => s.compile);
  const toggleFocusMode = useUIStore((s) => s.toggleFocusMode);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const toggleBottomPanel = useUIStore((s) => s.toggleBottomPanel);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const setFocusMode = useUIStore((s) => s.setFocusMode);

  const handlers = useCallback(
    (key: ShortcutKey): (() => void) => {
      const custom = customHandlers?.[key];
      if (custom) return custom;

      switch (key) {
        case "save":
          return saveDocument;
        case "compile":
          return compile;
        case "toggleFocusMode":
          return toggleFocusMode;
        case "toggleSidebar":
          return toggleSidebar;
        case "toggleBottomPanel":
          return toggleBottomPanel;
        case "toggleTheme":
          return toggleTheme;
        case "exitFocusMode":
          return () => setFocusMode(false);
        default:
          return DEFAULT_HANDLERS[key];
      }
    },
    [
      customHandlers,
      saveDocument,
      compile,
      toggleFocusMode,
      toggleSidebar,
      toggleBottomPanel,
      toggleTheme,
      setFocusMode,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;

      if (mod && e.key === "s") {
        e.preventDefault();
        handlers("save")();
        return;
      }
      if (mod && e.key === "Enter") {
        e.preventDefault();
        handlers("compile")();
        return;
      }
      if (mod && e.shiftKey && e.key === "f") {
        e.preventDefault();
        handlers("toggleFocusMode")();
        return;
      }
      if (mod && e.shiftKey && e.key === "b") {
        e.preventDefault();
        handlers("toggleSidebar")();
        return;
      }
      if (mod && e.key === "j") {
        e.preventDefault();
        handlers("toggleBottomPanel")();
        return;
      }
      if (mod && e.key === "\\") {
        e.preventDefault();
        handlers("toggleTheme")();
        return;
      }
      if (e.key === "Escape") {
        handlers("exitFocusMode")();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlers]);
}
